
import {
  computed, defineComponent, reactive, watch,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import { useSelecionarBase } from '@/core/composables/SelecionarBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoDadosRelatorio from '@/servicos/Relatorios/ServicoDadosRelatorio';
import { IItemConsultaRapida } from '@/core/models/Consulta/IItemConsultaRapida';

export default defineComponent({
  name: 'SelecionarRelatorioPainelVendas',
  components: { Icone },
  props: {
    codigoSelecionado: {
      type: [Number, String],
      default: undefined,
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Selecione o Relatório',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
    varios: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Relatório',
    },
    dataAttributeColuna: {
      type: String,
      default: '',
    },
    modulo: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update:codigoSelecionado', 'update:descricaoSelecionado', 'change', 'edit-report', 'delete-report'],
  setup(props, { emit }) {
    const {
      selecionarBase,
      verificacaoUltimaPesquisa,
      instanciaParametrosConsultaRapidaPorCodigo,
      instanciaParametrosConsultaRapidaPesquisa,
    } = useSelecionarBase(props, emit);

    const servicoDadosRelatorio = new ServicoDadosRelatorio();
    let debounce = 0;

    const state = reactive({
      buscandoDados: false,
    });

    function preencherDescricaoRelatorio(codigo: any) {
      if (!UtilitarioGeral.valorValido(codigo)) return;

      const item = selecionarBase.listaItensConsultaRapida.find((c: IItemConsultaRapida) => c.codigo === Number(codigo));
      if (item) {
        emit('update:descricaoSelecionado', item.textoIdentificacao);
      }
    }

    async function obterRelatorioPorCodigo(valor: any) {
      const valorBusca = Number(valor);
      if (!UtilitarioGeral.valorValido(valorBusca) || valorBusca <= 0) return;
      const existeNaLista = selecionarBase.listaItensConsultaRapida.some((c: IItemConsultaRapida) => c.codigo === valorBusca);

      if (!existeNaLista && props.descricaoSelecionado) {
        selecionarBase.listaItensConsultaRapida.push({
          codigo: valorBusca,
          textoIdentificacao: props.descricaoSelecionado,
          informacaoAdicional: '',
        });
      }

      if (existeNaLista) {
        preencherDescricaoRelatorio(valorBusca);
        return;
      }
      state.buscandoDados = true;
      try {
        const parametros = instanciaParametrosConsultaRapidaPorCodigo(valorBusca, true);
        const resultado = await servicoDadosRelatorio.consultaRapida(parametros, props.modulo);
        if (resultado && resultado.length > 0) {
          selecionarBase.listaItensConsultaRapida = resultado;
          preencherDescricaoRelatorio(valorBusca);
        }
      } catch (error) {
        console.error('Erro ao buscar relatório:', error);
      } finally {
        state.buscandoDados = false;
      }
    }

    async function pesquisarRelatorio(valorPesquisa: any) {
      selecionarBase.emDigitacao = true;
      if (!verificacaoUltimaPesquisa(valorPesquisa)) return;

      state.buscandoDados = true;
      clearTimeout(debounce);

      debounce = setTimeout(async () => {
        try {
          const parametros = instanciaParametrosConsultaRapidaPesquisa(valorPesquisa, true);
          selecionarBase.listaItensConsultaRapida = await servicoDadosRelatorio.consultaRapida(parametros, props.modulo);
          selecionarBase.valorUltimaPesquisa = valorPesquisa;
        } finally {
          state.buscandoDados = false;
        }
      }, 600);
    }

    const computedCodigoSelecionado = computed({
      get: () => (UtilitarioGeral.validaNumerico(props.codigoSelecionado)
        ? Number(props.codigoSelecionado)
        : undefined),
      set: (val) => {
        emit('update:codigoSelecionado', val);
      },
    });

    async function recarregarDados() {
      state.buscandoDados = true;
      try {
        const parametros = instanciaParametrosConsultaRapidaPesquisa('', true);
        selecionarBase.listaItensConsultaRapida = await servicoDadosRelatorio.consultaRapida(parametros, props.modulo);
        selecionarBase.valorUltimaPesquisa = '';

        if (UtilitarioGeral.valorValido(props.codigoSelecionado)) {
          const itemAindaExiste = selecionarBase.listaItensConsultaRapida.some(
            (opcao: IItemConsultaRapida) => opcao.codigo === Number(props.codigoSelecionado),
          );

          if (!itemAindaExiste) {
            emit('update:codigoSelecionado', undefined);
            emit('update:descricaoSelecionado', '');
            emit('change', undefined);
          }
        }
      } catch (error) {
        console.error('Erro ao recarregar lista de relatórios:', error);
      } finally {
        state.buscandoDados = false;
      }
    }

    watch(
      () => props.codigoSelecionado,
      (codigo) => {
        if (!UtilitarioGeral.valorValido(codigo)) return;
        obterRelatorioPorCodigo(codigo);
      },
      { immediate: true },
    );

    function change(val: any) {
      emit('change', val);
      preencherDescricaoRelatorio(val);
    }

    function onEdit(item: IItemConsultaRapida) {
      emit('edit-report', item);
    }

    function onDelete(item: IItemConsultaRapida) {
      emit('delete-report', item);
    }

    pesquisarRelatorio('');

    return {
      props,
      state,
      selecionarBase,
      computedCodigoSelecionado,
      pesquisarRelatorio,
      change,
      recarregarDados,
      onEdit,
      onDelete,
    };
  },
});
